import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';

const Account: React.FC = () => {
    const { t } = useTranslation();
    const { user, logoutUser } = useApp();
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(user?.name || '');
    const [editedEmail, setEditedEmail] = useState(user?.email || '');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleSave = async () => {
        setIsLoading(true);
        setMessage('');
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Here you would update the user in localStorage and context
        // For now, just show success message
        setMessage(t('account.profileUpdated'));
        setIsEditing(false);
        setIsLoading(false);
        
        // Reset to original values since we're not actually updating
        setTimeout(() => setMessage(''), 3000);
    };

    const handleCancel = () => {
        setEditedName(user?.name || '');
        setEditedEmail(user?.email || '');
        setIsEditing(false);
        setMessage('');
    };

    const stats = [
        { label: t('account.bookings'), value: '5' },
        { label: t('account.destinations'), value: '12' },
        { label: t('account.memberSince'), value: '2024' },
        { label: t('account.status'), value: 'Gold' }
    ];

    const recentActivity = [
        { type: 'booking', title: 'Hotel Atlantis Dubai', date: '2024-01-15', status: t('account.confirmed') },
        { type: 'search', title: 'Paris Hotels', date: '2024-01-10', status: t('account.viewed') },
        { type: 'booking', title: 'Flight to Tokyo', date: '2023-12-20', status: t('account.completed') }
    ];

    return (
        <div className="account-page">
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Header */}
                    <div className="account-header">
                        <div className="account-header-content">
                            <div className="profile-avatar">
                                <span className="avatar-text">
                                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                            </div>
                            <div className="profile-info">
                                <h1>{t('account.welcome')}, {user?.name}!</h1>
                                <p className="text-gray-600">{t('account.description')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="stats-grid">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                className="stat-card card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                whileHover={{ y: -5 }}
                            >
                                <div className="stat-value">{stat.value}</div>
                                <div className="stat-label">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="account-content">
                        {/* Profile Information */}
                        <motion.div
                            className="account-section card"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3, duration: 0.6 }}
                        >
                            <div className="section-header">
                                <h2>{t('account.profileInfo')}</h2>
                                {!isEditing && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={() => setIsEditing(true)}
                                    >
                                        {t('account.editProfile')}
                                    </button>
                                )}
                            </div>

                            {message && (
                                <motion.div
                                    className="success-message"
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {message}
                                </motion.div>
                            )}

                            {!isEditing ? (
                                <div className="profile-details">
                                    <div className="detail-row">
                                        <span className="detail-label">{t('account.fullName')}</span>
                                        <span className="detail-value">{user?.name}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">{t('account.email')}</span>
                                        <span className="detail-value">{user?.email}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">{t('account.userId')}</span>
                                        <span className="detail-value text-gray-500">#{user?.id}</span>
                                    </div>
                                </div>
                            ) : (
                                <motion.div
                                    className="profile-edit-form"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="form-group">
                                        <label htmlFor="edit-name">{t('account.fullName')}</label>
                                        <input
                                            id="edit-name"
                                            type="text"
                                            className="input"
                                            value={editedName}
                                            onChange={(e) => setEditedName(e.target.value)}
                                            placeholder={t('account.enterFullName')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="edit-email">{t('account.email')}</label>
                                        <input
                                            id="edit-email"
                                            type="email"
                                            className="input"
                                            value={editedEmail}
                                            onChange={(e) => setEditedEmail(e.target.value)}
                                            placeholder={t('account.enterEmail')}
                                        />
                                    </div>
                                    <div className="form-actions">
                                        <motion.button
                                            className="btn btn-primary"
                                            onClick={handleSave}
                                            disabled={isLoading}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {isLoading ? t('account.saving') : t('account.saveChanges')}
                                        </motion.button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleCancel}
                                            disabled={isLoading}
                                        >
                                            {t('account.cancelEdit')}
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Recent Activity */}
                        <motion.div
                            className="account-section card"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                        >
                            <div className="section-header">
                                <h2>{t('account.recentActivity')}</h2>
                            </div>
                            <div className="activity-list">
                                {recentActivity.map((activity, index) => (
                                    <motion.div
                                        key={index}
                                        className="activity-item"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
                                    >
                                        <div className="activity-icon">
                                            {activity.type === 'booking' ? '🏨' : '🔍'}
                                        </div>
                                        <div className="activity-content">
                                            <div className="activity-title">{activity.title}</div>
                                            <div className="activity-meta">
                                                <span className="activity-date">{activity.date}</span>
                                                <span className={`activity-status status-${activity.status.toLowerCase()}`}>
                                                    {activity.status}
                                                </span>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Account Actions */}
                        <motion.div
                            className="account-section card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7, duration: 0.6 }}
                        >
                            <div className="section-header">
                                <h2>{t('account.accountSettings')}</h2>
                            </div>
                            <div className="account-actions">
                                <button className="action-btn btn btn-secondary">
                                    <span className="action-icon">🔐</span>
                                    {t('account.changePassword')}
                                </button>
                                <button className="action-btn btn btn-secondary">
                                    <span className="action-icon">📱</span>
                                    {t('account.privacySettings')}
                                </button>
                                <button className="action-btn btn btn-secondary">
                                    <span className="action-icon">📧</span>
                                    {t('account.notifications')}
                                </button>
                                <motion.button
                                    className="action-btn btn-danger"
                                    onClick={logoutUser}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <span className="action-icon">🚪</span>
                                    {t('account.logout')}
                                </motion.button>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default Account;
